import { useAuth0 } from '@auth0/auth0-vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { getAuth0LoginRedirectOptions, useUserContextStore } from '@peanuds/app-shell';
import { useStorage } from '@peanuds/composables';
import type { GetContextsResponseDtoOutput } from '@peanuds/shared';

const AUTH_REDIRECT_KEY = 'auth_redirect_target';

type AccountOrganization = GetContextsResponseDtoOutput['data']['organizations'][number];

export interface AccountSwitcherItem {
  id: string;
  label: string;
  initial: string;
  isSelected: boolean;
}

const getOrganizationLabel = (organization: AccountOrganization) => {
  return organization.displayName || organization.name || organization.id;
};

const toAccountSwitcherItem = (
  organization: AccountOrganization,
  selectedOrganizationId: string | null,
): AccountSwitcherItem => {
  const label = getOrganizationLabel(organization);

  return {
    id: organization.id,
    label,
    initial: label.trim().charAt(0).toUpperCase(),
    isSelected: organization.id === selectedOrganizationId,
  };
};

export const useAccountSwitcher = () => {
  const route = useRoute();
  const userContextStore = useUserContextStore();
  const { organizations, resolvedOrganizationId, isLoaded, isLoading } =
    storeToRefs(userContextStore);
  const { loginWithRedirect } = useAuth0();
  const redirectStorage = useStorage<string>(AUTH_REDIRECT_KEY, { storage: 'session' });

  const switchingOrganizationId = ref<string | null>(null);
  const switchError = ref(false);

  const accounts = computed<AccountSwitcherItem[]>(() => {
    return organizations.value.map((organization) =>
      toAccountSwitcherItem(organization, resolvedOrganizationId.value),
    );
  });

  const shouldRender = computed(() => accounts.value.length > 1);
  const isSwitchingAccount = computed(() => switchingOrganizationId.value !== null);

  const ensureAccountsLoaded = async () => {
    if (isLoaded.value || isLoading.value) {
      return;
    }

    await userContextStore.ensureLoaded({ autoSelectSingleOrganization: false });
  };

  const switchAccount = async (organizationId: string) => {
    if (isSwitchingAccount.value || organizationId === resolvedOrganizationId.value) {
      return;
    }

    switchError.value = false;
    switchingOrganizationId.value = organizationId;

    try {
      redirectStorage.set(route.fullPath || '/');
      await loginWithRedirect(getAuth0LoginRedirectOptions(organizationId));
    } catch {
      switchError.value = true;
    } finally {
      switchingOrganizationId.value = null;
    }
  };

  onMounted(ensureAccountsLoaded);

  return {
    accounts,
    isSwitchingAccount,
    shouldRender,
    switchAccount,
    switchError,
  };
};
